import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFileUpload } from '@/shared/hooks/useFileUpload';
import { isPdfEncrypted } from '@/shared/utils/isPdfEncrypted';
import { UploadedUploadFile, UploadFile } from './types';
import { LocationAttachment } from '@/modules/location/domain/Attachment.ts';
import { ProductUseCaseDocument, ProductUseCaseImage } from '@schuettflix/client-file';

interface FileUploadManagerOptions {
    value: LocationAttachment[];
    onChange: (files: LocationAttachment[]) => void;
}

export function useFileUploadSession({ value, onChange }: FileUploadManagerOptions) {
    const { t } = useTranslation();
    const { uploadFile } = useFileUpload();

    const mapAttachmentToUpload = (attachment: LocationAttachment): UploadedUploadFile => ({
        file: { name: attachment.fileName, type: attachment.mimeType || 'application/octet-stream' },
        remotePath: attachment.url,
        status: 'uploaded' as const,
        progress: 100,
        url: attachment.originalUrl,
        id: attachment.id,
    });

    const [uploads, setUploads] = useState<UploadFile[]>(value.map(mapAttachmentToUpload));

    useEffect(() => {
        setUploads(value.map(mapAttachmentToUpload));
    }, [value]);

    const updateUpload = (fileUpdate: Partial<UploadFile> & Pick<UploadFile, 'file'>) => {
        setUploads(current =>
            current.map(upload => {
                if (upload.file === fileUpdate.file) {
                    return { ...upload, ...fileUpdate };
                }
                return upload;
            })
        );
    };

    const removeUpload = useCallback(
        (fileIdentifier: string) => {
            setUploads(current => current.filter(upload => upload.remotePath !== fileIdentifier));
            onChange(value.filter(file => file.url !== fileIdentifier));
        },
        [onChange, value]
    );

    const processFile = useCallback(
        async (file: File): Promise<UploadFile> => {
            const abortController = new AbortController();
            updateUpload({ file, status: 'uploading', abortController });

            if (file.type === 'application/pdf') {
                const isEncrypted = await isPdfEncrypted(file);
                if (isEncrypted) {
                    const failedFile: UploadFile = {
                        file,
                        status: 'failed',
                        error: t('common.upload.errors.pdfIsEncrypted', 'PDF is encrypted'),
                        progress: 0,
                        remotePath: '',
                    };
                    updateUpload(failedFile);
                    return failedFile;
                }
            }

            try {
                const urlData = await uploadFile(
                    file,
                    abortController.signal,
                    progress => {
                        updateUpload({ file, progress });
                    },
                    {
                        image: ProductUseCaseImage.OrderAttachmentImage,
                        document: ProductUseCaseDocument.OrderAttachmentDocument,
                    }
                );

                const uploadedFile: UploadFile = {
                    file,
                    status: 'uploaded',
                    remotePath: urlData.remotePath,
                    url: urlData.original_url,
                    progress: 100,
                    id: urlData.id,
                };
                updateUpload(uploadedFile);
                return uploadedFile;
            } catch (error) {
                const failedFile: UploadFile = {
                    file,
                    status: 'failed',
                    error: error instanceof Error ? error.message : 'Upload failed',
                    progress: 0,
                    remotePath: '',
                };
                updateUpload(failedFile);
                return failedFile;
            }
        },
        [t, uploadFile]
    );

    const mapToAttachment = (upload: UploadedUploadFile): LocationAttachment => ({
        fileName: upload.file.name,
        url: upload.remotePath,
        uploadedAt: new Date(),
        mimeType: upload.file.type,
        originalUrl: upload.url,
        id: upload.id,
    });

    const uploadFiles = useCallback(
        async (files: File[]) => {
            const pendingFiles = files.map(file => ({
                file,
                progress: 0,
                remotePath: '',
                status: 'pending' as const,
            }));

            setUploads(current => [...current, ...pendingFiles]);

            const results = await Promise.all(pendingFiles.map(({ file }) => processFile(file)));
            const successfulUploads = results
                .filter(upload => upload.status === 'uploaded' && !!upload.id?.trim() && !!upload.url?.trim())
                .map<UploadedUploadFile>(f => ({ ...f, status: 'uploaded', progress: 100, id: f.id!, url: f.url! }));

            if (successfulUploads.length > 0) {
                const mappedFiles = successfulUploads.map(mapToAttachment);

                onChange([...value, ...mappedFiles]);
            }
        },
        [processFile, value, onChange]
    );

    const addFilesToUpload = useCallback(
        (files: FileList | File[]) => {
            const fileArray = Array.from(files);
            if (fileArray.length > 0) {
                void uploadFiles(fileArray);
            }
            return false;
        },
        [uploadFiles]
    );

    return {
        uploads,
        addFilesToUpload,
        removeUpload,
    };
}
